import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "sonner";
import { CalendarDays, Clock, Users, IndianRupee, ArrowUpRight, Stethoscope } from "lucide-react";
import { getDashboardSummary } from "../../api/api";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

const getGreeting = (now) => {
  const hour = now.hour();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const TICKERS = [
  { key: "total_appointments", label: "Total Appointments", icon: CalendarDays },
  { key: "today_visits", label: "Today's OPD Visits", icon: Clock },
  { key: "registered_patients", label: "Registered Patients", icon: Users },
  { key: "today_revenue", label: "Today's Revenue", icon: IndianRupee, isCurrency: true },
];

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [now, setNow] = useState(() => moment());
  const navigate = useNavigate();

  useEffect(() => {
    getDashboardSummary()
      .then((res) => setSummary(res.data))
      .catch((err) => toast.error(err?.message || "Could not load dashboard data."));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  const total = summary
    ? summary.snapshot.completed + summary.snapshot.cancelled + summary.snapshot.upcoming
    : 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">{now.format("dddd · D MMMM YYYY · h:mm:ss A")}</p>
          <h2 className="font-serif text-3xl font-semibold">{getGreeting(now)}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/appointments")}>
            View appointments
          </Button>
          <Button onClick={() => navigate("/opd")}>New OPD booking</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {TICKERS.map((t) => {
          const Icon = t.icon;
          const value = summary ? summary[t.key] : "—";
          return (
            <Card key={t.key}>
              <CardContent className="flex items-center gap-3 p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-xs text-muted-foreground">{t.label}</p>
                  <p className="font-serif text-xl font-semibold">
                    {t.isCurrency && value !== "—" ? `₹${value.toLocaleString("en-IN")}` : value}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Today's timeline</CardTitle>
            <button onClick={() => navigate("/appointments")} className="flex items-center gap-1 text-sm text-primary">
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </CardHeader>
          <CardContent className="space-y-3">
            {!summary || summary.timeline.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No confirmed appointments today.</p>
            ) : (
              summary.timeline.map((item) => (
                <div key={item.id} className="flex items-center justify-between rounded-md border border-border p-3">
                  <div className="flex items-center gap-3">
                    <span className="w-16 shrink-0 text-sm font-medium">{item.slot_time}</span>
                    <div>
                      <p className="text-sm font-medium">{item.patient}</p>
                      <p className="text-xs text-muted-foreground">{item.doctor}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.delay && <Badge variant="warning">+{item.delay.minutes}m</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => navigate("/appointments")}>
                      View
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Snapshot</CardTitle>
          </CardHeader>
          <CardContent>
            {summary && (
              <>
                <ul className="mb-3 space-y-2 text-sm">
                  <li className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-secondary" /> Completed
                    </span>
                    <b>{summary.snapshot.completed}</b>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-destructive" /> Cancelled
                    </span>
                    <b>{summary.snapshot.cancelled}</b>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-primary" /> Upcoming
                    </span>
                    <b>{summary.snapshot.upcoming}</b>
                  </li>
                </ul>
                <div className="flex h-2 overflow-hidden rounded-full bg-muted">
                  <span className="bg-secondary" style={{ width: `${pct(summary.snapshot.completed)}%` }} />
                  <span className="bg-destructive" style={{ width: `${pct(summary.snapshot.cancelled)}%` }} />
                  <span className="bg-primary" style={{ width: `${pct(summary.snapshot.upcoming)}%` }} />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <button
          onClick={() => navigate("/opd")}
          className="rounded-lg border border-border bg-card p-5 text-left shadow-sm transition hover:shadow-md"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Stethoscope className="h-4 w-4" />
          </span>
          <h4 className="mt-3 font-serif text-lg font-semibold">OPD Booking</h4>
          <p className="mt-1 text-sm text-muted-foreground">
            Book a new out-patient consultation in a few guided steps.
          </p>
        </button>
        <button
          onClick={() => navigate("/appointments")}
          className="rounded-lg border border-border bg-card p-5 text-left shadow-sm transition hover:shadow-md"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary/10 text-secondary">
            <CalendarDays className="h-4 w-4" />
          </span>
          <h4 className="mt-3 font-serif text-lg font-semibold">Patient Appointments</h4>
          <p className="mt-1 text-sm text-muted-foreground">
            Track, filter and manage every appointment across your clinic.
          </p>
        </button>
      </div>
    </div>
  );
}
